import { type V2, smoothstep } from '../../core/math';
import { Rng } from '../../core/rng';
import { DungeonBuilder, type DungeonKit } from '../../zones/DungeonBuilder';
import type { ZoneLayout } from '../../zones/types';

const TIDE = 0xa8d8d4;
const TIDE_ROCK = 0x9cc8c4;
const SEA_WATER = 0x1e6a70;

/** Sea-worn reliquary kit: dark walls washed teal, barnacled rock, braziers. */
const KIT: DungeonKit = {
  wall: { model: 'dungeon_wall', length: 8, depth: 1.2, scale: 0.8, tint: 0xb8e0dc },
  post: { model: 'pillar_intact', every: 3, scale: [0.85, 1.0], tint: TIDE },
  light: { model: 'brazier', every: 3 },
  low: [
    { model: 'pillar_ruin', scale: [0.5, 0.75], tint: TIDE, weight: 2 },
    { model: 'rock_b', scale: [0.7, 1.1], tint: TIDE_ROCK },
  ],
  rocks: [
    { model: 'cliff_chunk', r: 2.9, scale: [0.5, 0.85], tint: TIDE_ROCK, weight: 3 },
    { model: 'rock_a', r: 1.1, scale: [1.1, 1.8], tint: TIDE_ROCK },
    { model: 'rock_c', r: 0.7, scale: [1.0, 1.8], tint: TIDE_ROCK },
  ],
  clutter: [{ model: 'rock_b', scale: [0.3, 0.5], tint: TIDE_ROCK, weight: 2 }, { model: 'mushroom_cluster', scale: [0.4, 0.7], tint: 0x80e0d0 }, { model: 'bones' }],
  clutterChance: 0.3,
};

function vaultBuilder(gate: 1 | 2, subtitle: string, depth: number): DungeonBuilder {
  return new DungeonBuilder(
    {
      id: `deepvault_${gate}`, name: 'The Sunken Reliquary', subtitle, kind: 'deepvault', biome: 'abyss', levelRange: [30, 30], music: 'raid',
      width: 120, depth, seed: 2700 + gate, ground: ['tiles', 'cobble', 'rock', 'moss'], groundScale: [4, 4, 7, 5],
      ambient: 'motes', mapTint: 0x10282c, wallHeight: 8, nearWallHeight: 1.1, accentAmount: 0.55, kit: KIT,
    },
    new Rng(`deepvault_${gate}`),
  );
}

/** Resupply landing at the gate start: supplies and the station spot. */
function resupply(d: DungeonBuilder, x: number, z: number): void {
  d.spawn('resupply', x + 3.2, z + 1.4, Math.PI);
  d.prop('crate', x + 5.2, z + 3.4, { rot: 0.2, collider: { w: 0.9, d: 0.9 } });
  d.prop('crate', x + 6.0, z + 2.5, { rot: 0.9, collider: { w: 0.9, d: 0.9 } });
  d.prop('barrel', x + 4.3, z + 4.2, { collider: { r: 0.45 } });
  d.prop('barrel', x - 5.4, z + 3.6, { collider: { r: 0.45 } });
  d.prop('notice_board', x - 5.3, z + 0.6, { rot: Math.PI / 2, collider: { w: 2.1, d: 0.5 } });
}

/**
 * THE SUNKEN RELIQUARY — Gate 1: the Drowned Warden. From the resupply
 * landing a short flooded corridor (two drowned-thrall galleries, shallow
 * water) leads to a round arena (r 22). Tidal pillars rise at `pillar_0..3`
 * (NE, SE, SW, NW at r 12.5); the Warden spawns at `boss`. After the kill the
 * way down (`dv1_next`, hidden until `deepvault_1_cleared`) opens to Gate 2.
 */
export function buildDeepvault1(): ZoneLayout {
  const d = vaultBuilder(1, 'Gate I — The Drowned Warden', 170);
  d.hall(0, 66, 8, 7, { y: 0.8, path: true });
  d.room(0, 44, 7, { y: -0.55, path: true });
  d.room(-6, 28, 7, { y: -0.6, path: true });
  d.room(0, -20, 22, { y: 0.2, path: true });
  d.corridor([{ x: 0, z: 60 }, { x: 0, z: 44 }, { x: -6, z: 28 }, { x: 0, z: 12 }, { x: 0, z: 0 }], 7, { path: true });
  d.runner([{ x: 0, z: 70 }, { x: 0, z: 44 }, { x: -6, z: 28 }, { x: 0, z: 12 }, { x: 0, z: -8 }], 2.6);
  // Shallow flood water pools in the sunken galleries (walkable).
  d.water.push({ shape: { kind: 'sheet' }, level: -0.3, deep: false, color: SEA_WATER });

  d.spawn('start', 0, 64, Math.PI);
  resupply(d, 0, 64);
  d.pack({ id: 'dv1_thralls_a', monsters: [{ def: 'drowned_thrall', count: 4 }], x: 0, z: 44, radius: 4.5, level: 30 });
  d.pack({ id: 'dv1_thralls_b', monsters: [{ def: 'drowned_thrall', count: 5 }], x: -6, z: 28, radius: 4.5, level: 30 });
  d.pack({ id: 'dv1_thralls_c', monsters: [{ def: 'drowned_thrall', count: 3 }], x: 0, z: 9, radius: 2.5, level: 30 });
  d.arch(0, 1.6, Math.PI, 7, TIDE);

  d.spawn('boss', 0, -22, 0);
  DungeonBuilder.ring(0, -20, 12.5, 4, 1.5).forEach((p, i) => {
    d.spawn(`pillar_${i}`, p.x, p.z, 0);
    d.paintCircle(p.x, p.z, 2.1, 1, 0.6);
  });
  d.paintRing(0, -20, 5, 5.8, 3, 0.5);
  d.litPortal({ id: 'dv1_next', to: 'deepvault_2', toSpawn: 'start', x: 0, z: -37.5, radius: 2.4, label: 'Descend to the Tide Sanctum', kind: 'instance', requires: 'deepvault_1_cleared' }, Math.PI);
  d.prop('statue_hero', 0, -46.5, { scale: 1.0, tint: 0x90c8c0 });
  return d.build();
}

/** Tide ring of Deepvault Gate 2: platform r < 13.8, deep water 13.8–21.2, walkway to 29. */
export const TIDE_SANCTUM = { x: 0, z: 0, platform: 13.8, water: 21.2, outer: 29 } as const;

/**
 * THE SUNKEN RELIQUARY — Gate 2: the Tide Siren. The sanctum is a round
 * central platform ringed by deep water, crossed by four diagonal causeways,
 * inside an outer walkway (see TIDE_SANCTUM). The Siren spawns at `boss`
 * (centre); adds rise from the water onto the walkway at `add_0..5`.
 * `dv2_exit` (hidden until `deepvault_2_cleared`) returns to Havenreach.
 */
export function buildDeepvault2(): ZoneLayout {
  const d = vaultBuilder(2, 'Gate II — The Tide Siren', 140);
  d.hall(0, 52, 7, 5, { y: 0.6, path: true });
  d.room(0, 0, TIDE_SANCTUM.outer, { y: 0, path: true });
  d.corridor([{ x: 0, z: 48 }, { x: 0, z: 27 }], 6, { path: true });
  // Water ring with four diagonal causeways; the platform stands proud of the tide.
  d.addHeight((x, z) => {
    const r = Math.hypot(x, z);
    if (r > TIDE_SANCTUM.water + 2) return 0;
    const ring = smoothstep(TIDE_SANCTUM.platform - 0.6, TIDE_SANCTUM.platform + 0.8, r) * (1 - smoothstep(TIDE_SANCTUM.water - 0.8, TIDE_SANCTUM.water + 0.6, r));
    const causeway = 1 - smoothstep(2.0, 2.7, Math.min(Math.abs(x - z), Math.abs(x + z)) / Math.SQRT2);
    const platform = 0.6 * (1 - smoothstep(TIDE_SANCTUM.platform - 0.6, TIDE_SANCTUM.platform + 0.6, r));
    return platform + ring * (causeway * 0.25 - (1 - causeway) * 2.8);
  });
  d.water.push({ shape: { kind: 'sheet' }, level: -0.7, deep: true, color: SEA_WATER });
  // "Pizza" seams: eight wedges on the platform for the rotating-floor mechanic.
  const seams: [V2, V2][] = [
    [{ x: 0, z: -13 }, { x: 0, z: 13 }],
    [{ x: -13, z: 0 }, { x: 13, z: 0 }],
    [{ x: -9.2, z: -9.2 }, { x: 9.2, z: 9.2 }],
    [{ x: -9.2, z: 9.2 }, { x: 9.2, z: -9.2 }],
  ];
  for (const seam of seams) d.paintLine(seam, 0.35, 1, 0.3);
  d.paintRing(0, 0, 12.4, 13.2, 3, 0.4);
  for (const h of [1.5, 4.5, 7.5, 10.5]) {
    for (const r of [TIDE_SANCTUM.platform + 0.6, TIDE_SANCTUM.water - 0.2]) {
      const c = DungeonBuilder.clock(0, 0, r, h);
      const a = (h / 12) * Math.PI * 2;
      const t = { x: Math.cos(a), z: Math.sin(a) };
      for (const s of [-1, 1]) d.prop('pillar_ruin', c.x + t.x * 2.9 * s, c.z + t.z * 2.9 * s, { scale: 0.8, rot: a, tint: TIDE });
    }
  }

  d.spawn('start', 0, 51, Math.PI);
  resupply(d, 0, 51);
  d.spawn('boss', 0, 0, 0);
  DungeonBuilder.ring(0, 0, 25, 6, 1).forEach((p, i) => d.spawn(`add_${i}`, p.x, p.z, Math.atan2(-p.x, -p.z)));
  d.litPortal({ id: 'dv2_exit', to: 'havenreach', toSpawn: 'fromInstance', x: 0, z: -25.2, radius: 2.4, label: 'Return to Havenreach', kind: 'exit', requires: 'deepvault_2_cleared' }, Math.PI);
  d.prop('statue_hero', 0, -34, { scale: 1.1, tint: 0x90c8c0 });
  return d.build();
}
